export const PALETTE = {
	bg: "#05060C",
	surface: "#0E1018",
	surfaceElevated: "#14161F",
	border: "rgba(255,255,255,0.06)",
	borderAccent: "rgba(100,211,239,0.32)",
	accent: "#5FE0F5",
	accentDim: "rgba(100,211,239,0.12)",
	accentGlow: "rgba(100,211,239,0.22)",
	success: "#03C295",
	successDim: "rgba(3,194,149,0.14)",
	muted: "#8B8FA8",
	textPrimary: "#F0F1F7",
	textSecondary: "#B8BCD0",
	textMuted: "#7E8299",
	danger: "#FF7A7A",
	dangerDim: "rgba(255,122,122,0.14)",
	warning: "#FFD27A",
	warningDim: "rgba(255,210,122,0.12)",
	skeletonBase: "#15171F",
	skeletonHighlight: "#1E212C",
} as const;

export const DASHBOARD_PALETTE = {
	...PALETTE,
	dashboardBase: "#171832",
	bg: "#171832",
	surface: "#1E203C",
	surfaceElevated: "#232448",
	textPrimary: "#EEF0FA",
	textSecondary: "#C4C8E8",
	textMuted: "#8E92B8",
	muted: "#9BA0C8",
	cardBg: "#1A1C38",
	cardBgHover: "#1E2042",
	/** Borders with a blue tint, not flat neutral gray */
	cardBorder: "rgba(110, 125, 220, 0.14)",
	cardBorderHover: "rgba(120, 190, 255, 0.35)",
	border: "rgba(110, 125, 220, 0.14)",
	borderAccent: "rgba(100, 195, 240, 0.32)",
	/** Softer cyan accent to blend with the navy background */
	accent: "#6EC8F0",
	accentDim: "rgba(100, 190, 235, 0.12)",
	accentGlow: "rgba(100, 200, 255, 0.2)",
	success: "#03C295",
	successDim: "rgba(3,194,149,0.14)",
	warning: "#E8C86A",
	warningDim: "rgba(232, 200, 106, 0.12)",
	danger: "#FF7A7A",
	dangerDim: "rgba(255,122,122,0.14)",
	skeletonBase: "#1B1D3A",
	skeletonHighlight: "#252850",
	rowInset: "#14162E",
	borderButton: "#343868",
	/** Loading overlay — same hue family as #171832 */
	backdropOverlay: "rgba(23, 24, 50, 0.92)",
	/** Card corner glow — stays in the blue range, tied to the base */
	glowOrbAccent:
		"radial-gradient(circle at center, rgba(100, 190, 230, 0.14), transparent 70%)",
	glowOrbSuccess:
		"radial-gradient(circle at center, rgba(3, 194, 149, 0.09), transparent 70%)",
	glowOrbWarning:
		"radial-gradient(circle at center, rgba(232, 200, 106, 0.08), transparent 70%)",
	amountIconBg: "rgba(100, 190, 230, 0.16)",
	/** Amount card: deep blue → navy gradient, avoids pure black */
	amountGradient:
		"linear-gradient(145deg, rgba(45, 52, 105, 0.45) 0%, rgba(30, 32, 65, 0.55) 45%, rgba(23, 24, 50, 0.65) 100%)",
	amountBorder: "rgba(100, 160, 255, 0.22)",
	/** Chart grid — subtle blue tint, blends with #171832 */
	chartGridLine: "rgba(90, 110, 200, 0.09)",
	chartGridLineGreen: "rgba(3, 194, 149, 0.07)",
	borderHoverSuccess: "rgba(3, 194, 149, 0.38)",
	borderHoverWarning: "rgba(232, 200, 106, 0.36)",
	// Chart colors
	chartCyan: "#6EC8F0",
	chartPurple: "#C9A0FF",
	chartGreen: "#88FFA2",
	chartOrange: "#FFB89E",
	chartYellow: "#E8C86A",
	// Status colors
	statusSuccess: "#03C295",
	statusWarning: "#E8C86A",
	statusDanger: "#FF7A7A",
	statusInfo: "#6EC8F0",
	gradientAccent: "linear-gradient(135deg, #6EC8F0 0%, #03C295 100%)",
	gradientSurface: "linear-gradient(180deg, #232448 0%, #171832 100%)",
} as const;


/** Tokens for Sponsor tabs and dialogs — #2D2E45 axis */

export const SPONSOR_PALETTE = {
  borderSubtle: "rgba(255,255,255,0.07)",
  borderStrong: "#434460",
  accent: "#64D3EF",
  accentDim: "rgba(100,211,239,0.12)",
  accentHover: "rgba(100,211,239,0.06)",
  dialogBg: "#2D2E45",
  textPrimary: "#EBEBF5",
  textSecondary: "#C7C6C8",
  textMuted: "#6D6E8C",
  rowStripe: "rgba(255,255,255,0.025)",
  rowHover: "rgba(100,211,239,0.05)",
  inputBg: "rgba(255,255,255,0.04)",
  inputBorder: "#434460",
  skeletonBase: "#2A2B47",
  warning: "#FFD27A",
  warningDim: "rgba(255,210,122,0.12)",
  danger: "#FF6B6B",
  dangerDim: "rgba(255,107,107,0.12)",
  success: "#03C295",
  successDim: "rgba(3,194,149,0.12)",
} as const;

export const REPORT_PALETTE = {
  cardBg: "#111220",
  headerBg: "rgba(0,0,0,0.2)",
  border: "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.12)",
  accent: "#5FE0F5",
  accentDim: "rgba(95,224,245,0.08)",
  accentHover: "rgba(95,224,245,0.05)",
  textPrimary: "#F0F1F7",
  textSecondary: "#B8BCD0",
  muted: "#8B8FA8",
  rowStripe: "rgba(255,255,255,0.025)",
  rowHover: "rgba(95,224,245,0.06)",
  skeletonBase: "#15171F",
  inputBg: "rgba(255,255,255,0.04)",
  danger: "#FF6B6B",
  dangerDim: "rgba(255,107,107,0.1)",
  gradientBanner: "linear-gradient(90deg, #5FE0F5 0%, #C9A0FF 100%)",
  status: {
    new:      { bg: "rgba(110,200,240,0.15)", color: "#6EC8F0" },
    checking: { bg: "rgba(232,200,106,0.15)", color: "#E8C86A" },
    fixed:    { bg: "rgba(3,194,149,0.15)",   color: "#03C295" },
    closed:   { bg: "rgba(255,255,255,0.08)", color: "#8B8FA8" },
  },
} as const;

export const POLICY_PALETTE = {
  surface: {
    page: "linear-gradient(145deg, rgba(13,17,48,0.96) 0%, rgba(19,23,56,0.98) 100%)",
    card: "linear-gradient(145deg, rgba(24,29,68,0.96) 0%, rgba(18,23,55,0.98) 100%)",
    cardMuted: "rgba(255,255,255,0.02)",
    dialog: "#171c44",
    editor: "#121736",
    toolbar: "#232a5f",
    border: "#38407a",
  },
  text: {
    primary: "#F5F8FF",
    secondary: "#AAB4E5",
    muted: "#7A84B8",
  },
  action: {
    primary: "#62D0FF",
    primaryHover: "#4FBFEF",
    primarySoft: "rgba(98,208,255,0.14)",
    secondaryBorder: "#4C568A",
    secondaryHover: "rgba(255,255,255,0.06)",
    disabledBg: "#343B66",
    disabledText: "#7D84B1",
  },
  status: {
    success: "#48D4A0",
    warning: "#F0BF56",
    pdf: "#EF6A6A",
    docx: "#77AEFF",
  },
  glow: {
    cardHover: "0 14px 34px rgba(54, 117, 255, 0.24)",
    accent: "0 0 0 1px rgba(98,208,255,0.5)",
  },
} as const;
